import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Calendar, Plus, MapPin, User, Clock, AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { useSuccessToast } from '../../store/successToastStore';
import { getAppointments, createAppointment, updateAppointment, deleteAppointment } from '../../api/appointments';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
import { InputField } from '../../components/InputField';
interface Appointment {
  id: string;
  title: string;
  appointmentDate: string;
  location?: string;
  doctorName?: string;
  notes?: string;
}
type FilterTab = 'upcoming' | 'past' | 'all';
export function AppointmentsList() {
  const {
    token
  } = useAuth();
  const {
    showToast
  } = useSuccessToast();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filter, setFilter] = useState<FilterTab>('upcoming');
  // Bottom Sheet State
  const [showSheet, setShowSheet] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Delete Confirmation State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Form State
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    doctorName: '',
    location: '',
    notes: ''
  });
  // Pull to refresh
  const touchStartY = useRef(0);
  const fetchAppointments = async () => {
    if (!token) return;
    setError(false);
    try {
      const res = await getAppointments(token);
      setAppointments(res || []);
    } catch (err) {
      setError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    fetchAppointments();
  }, [token]);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (window.scrollY === 0 && touchStartY.current > 0) {
      const touchEndY = e.changedTouches[0].clientY;
      if (touchEndY - touchStartY.current > 60) {
        setIsRefreshing(true);
        fetchAppointments();
      }
    }
    touchStartY.current = 0;
  };
  const filteredAppointments = useMemo(() => {
    const now = new Date().getTime();
    const sorted = [...appointments].sort((a, b) => new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime());
    if (filter === 'upcoming') {
      return sorted.filter(a => new Date(a.appointmentDate).getTime() >= now);
    }
    if (filter === 'past') {
      return sorted.filter(a => new Date(a.appointmentDate).getTime() < now).reverse();
    }
    return sorted;
  }, [appointments, filter]);
  const getRelativeTimeLabel = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    // Reset times to compare just dates
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffTime = dateOnly.getTime() - nowOnly.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 1 && diffDays < 7) return `In ${diffDays} days`;
    if (diffDays < -1 && diffDays > -7) return `${Math.abs(diffDays)} days ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const openCreateSheet = () => {
    setEditingAppointment(null);
    const now = new Date();
    // Default to tomorrow at 9 AM
    now.setDate(now.getDate() + 1);
    now.setHours(9, 0, 0, 0);
    setFormData({
      title: '',
      date: now.toISOString().split('T')[0],
      time: '09:00',
      doctorName: '',
      location: '',
      notes: ''
    });
    setShowDeleteConfirm(false);
    setShowSheet(true);
  };
  const openEditSheet = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    const dateObj = new Date(appointment.appointmentDate);
    // Format date as YYYY-MM-DD for input
    const dateStr = dateObj.toISOString().split('T')[0];
    // Format time as HH:MM for input
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}`;
    setFormData({
      title: appointment.title,
      date: dateStr,
      time: timeStr,
      doctorName: appointment.doctorName || '',
      location: appointment.location || '',
      notes: appointment.notes || ''
    });
    setShowDeleteConfirm(false);
    setShowSheet(true);
  };
  const handleSave = async () => {
    if (!token || !formData.title || !formData.date || !formData.time) return;
    setIsSaving(true);
    try {
      // Combine date and time
      const dateTimeStr = `${formData.date}T${formData.time}:00`;
      const appointmentDate = new Date(dateTimeStr).toISOString();
      const payload = {
        title: formData.title,
        appointmentDate,
        doctorName: formData.doctorName || undefined,
        location: formData.location || undefined,
        notes: formData.notes || undefined
      };
      if (editingAppointment) {
        await updateAppointment(token, editingAppointment.id, payload);
        showToast('Appointment updated');
      } else {
        await createAppointment(token, payload);
        showToast('Appointment created');
      }
      setShowSheet(false);
      fetchAppointments();
    } catch (err) {
      // Handle error (could add error toast here)
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!token || !editingAppointment) return;
    setIsDeleting(true);
    try {
      await deleteAppointment(token, editingAppointment.id);
      showToast('Appointment deleted');
      setShowDeleteConfirm(false);
      setShowSheet(false);
      fetchAppointments();
    } catch (err) {
      // Handle error
    } finally {
      setIsDeleting(false);
    }
  };
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0 min-h-screen" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-504" data-mp="122aeq3y9e7er">
      {isRefreshing && <div className="flex justify-center py-2" data-id="element-505" data-mp="33tdqwxnsru1f">
          <RefreshCw size={20} className="text-primary animate-spin" data-id="element-506" data-mp="33up4tqayii69" />
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-507" data-mp="37vhdahc08j1h">
        <h1 className="text-[24px] font-semibold text-text-primary" data-id="element-508" data-mp="30bq118mbz7vj">
          Appointments
        </h1>
        <button onClick={openCreateSheet} className="w-[40px] h-[40px] rounded-full bg-primary flex items-center justify-center hover:bg-primary/90 transition-colors shadow-sm" aria-label="Add Appointment" data-id="element-509" data-mp="2vkhuckcdy0m2">
          <Plus size={24} className="text-white" data-id="element-510" data-mp="2mhyt24o8zw1x" />
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar space-x-2 mb-6" data-id="element-511" data-mp="3j7ybcqu7a8xp">
        {[{
        id: 'upcoming',
        label: 'Upcoming'
      }, {
        id: 'past',
        label: 'Past'
      }, {
        id: 'all',
        label: 'All'
      }].map(f => <button key={f.id} onClick={() => setFilter(f.id as FilterTab)} className={`flex-shrink-0 h-[36px] px-5 rounded-pill text-[14px] font-medium transition-colors duration-200 ${filter === f.id ? 'bg-primary text-white shadow-sm' : 'bg-white border border-border text-text-secondary hover:bg-border/30'}`} data-id="element-512" data-mp="1yvs8yk1jupvc">
            {f.label}
          </button>)}
      </div>

      {/* Content */}
      {isLoading ? <div className="space-y-4" data-id="element-513" data-mp="3ozlru4pgjj4s">
          {[1, 2, 3].map(i => <SkeletonBlock key={i} height="120px" borderRadius="16px" data-id="element-514" data-mp="gizebglyywis" />)}
        </div> : error ? <div className="bg-danger/10 border border-danger/20 rounded-[16px] p-6 text-center" data-id="element-515" data-mp="li3krzhieg5f">
          <AlertTriangle size={32} className="text-danger mx-auto mb-3" data-id="element-516" data-mp="6tfqrxnbaehl" />
          <h3 className="text-[16px] font-semibold text-text-primary mb-2" data-id="element-517" data-mp="bs0tc3q1da97">
            Failed to load appointments
          </h3>
          <p className="text-[14px] text-text-secondary mb-4" data-id="element-518" data-mp="zhbl4vperub7">
            There was a problem connecting to the server.
          </p>
          <Button onClick={fetchAppointments} className="mx-auto" data-id="element-519" data-mp="149mm81vvxm5c">
            Try Again
          </Button>
        </div> : filteredAppointments.length === 0 ? <div className="py-16 flex flex-col items-center justify-center text-center" data-id="element-520" data-mp="2jykuso9jd5xa">
          <div className="w-[140px] h-[140px] mb-6 relative" data-id="element-521" data-mp="2xragssp2eaue">
            <svg viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-522" data-mp="2xsva6ezn7nqo">
              <rect x="20" y="30" width="100" height="90" rx="16" fill="#F3E8FF" data-id="element-523" data-mp="25z33a14uvek8" />
              <path d="M20 60H120" stroke="#6B21E8" strokeWidth="4" data-id="element-524" data-mp="2f0t2ht4zbhsk" />
              <rect x="35" y="15" width="12" height="30" rx="6" fill="#00D4D4" data-id="element-525" data-mp="2a1g4qdckkqb7" />
              <rect x="93" y="15" width="12" height="30" rx="6" fill="#00D4D4" data-id="element-526" data-mp="3qhj4f02ddngd" />
              <circle cx="45" cy="85" r="6" fill="#6B21E8" opacity="0.5" data-id="element-527" data-mp="19na3tp6zaf80" />
              <circle cx="70" cy="85" r="6" fill="#6B21E8" opacity="0.5" data-id="element-528" data-mp="14mmink5fkslp" />
              <circle cx="95" cy="85" r="6" fill="#6B21E8" opacity="0.5" data-id="element-529" data-mp="zul0jhll07nr" />
              <circle cx="45" cy="105" r="6" fill="#6B21E8" opacity="0.5" data-id="element-530" data-mp="vu0yq0q2cfva" />
              <circle cx="70" cy="105" r="6" fill="#6B21E8" opacity="0.5" data-id="element-531" data-mp="vryc7qo4d4vb" />
            </svg>
          </div>
          <h3 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-532" data-mp="lvl8s12rvcnd">
            No appointments yet
          </h3>
          <p className="text-[15px] text-text-secondary max-w-[280px] mb-8" data-id="element-533" data-mp="gvywbturq0fm">
            {filter === 'past' ? "You don't have any past appointments." : 'No appointments yet. Tap + to schedule one.'}
          </p>
          {filter !== 'past' && <Button onClick={openCreateSheet} data-id="element-534" data-mp="278y3ciyqddnr">
              Add your first appointment
            </Button>}
        </div> : <div className="space-y-4" data-id="element-535" data-mp="3p5hvdmy7wisz">
          {filteredAppointments.map(apt => {
        const isPast = new Date(apt.appointmentDate).getTime() < new Date().getTime();
        const dateObj = new Date(apt.appointmentDate);
        return <div key={apt.id} onClick={() => openEditSheet(apt)} className={`bg-white rounded-[16px] shadow-sm border-l-4 border-primary p-4 cursor-pointer transition-all hover:shadow-md hover:-translate-y-[2px] ${isPast ? 'opacity-60 grayscale-[0.2]' : ''}`} data-id="element-536" data-mp="6t3b0q3ls29o">
                <div className="flex justify-between items-start mb-2" data-id="element-537" data-mp="3f7c0vqeqqey5">
                  <h3 className="text-[16px] font-semibold text-text-primary line-clamp-1 pr-4" data-id="element-538" data-mp="3k7zmtxo445li">
                    {apt.title}
                  </h3>
                  <span className={`text-[12px] font-medium whitespace-nowrap px-2 py-1 rounded-pill ${isPast ? 'bg-border text-text-secondary' : 'bg-primary/10 text-primary'}`} data-id="element-539" data-mp="uhwdl5eqd8d9">
                    {getRelativeTimeLabel(apt.appointmentDate)}
                  </span>
                </div>

                <div className="space-y-2 mt-3" data-id="element-540" data-mp="35ijll04d6vjn">
                  <div className="flex items-center text-text-secondary text-[14px]" data-id="element-541" data-mp="35h8n9ftoufzx">
                    <Clock size={16} className="mr-2 flex-shrink-0 text-primary/70" data-id="element-542" data-mp="30ki7ktlspmf1" />
                    <span data-id="element-543" data-mp="2crjfmjmh3hpc">
                      {dateObj.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
                      {' at '}
                      {dateObj.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                })}
                    </span>
                  </div>

                  {apt.doctorName && <div className="flex items-center text-text-secondary text-[14px]" data-id="element-544" data-mp="4lr2qpy5kji1">
                      <User size={16} className="mr-2 flex-shrink-0 text-primary/70" data-id="element-545" data-mp="9cpo43y007s5" />
                      <span className="line-clamp-1" data-id="element-546" data-mp="3rstf3vmzccko">{apt.doctorName}</span>
                    </div>}

                  {apt.location && <div className="flex items-center text-text-secondary text-[14px]" data-id="element-547" data-mp="ncilgi415m93">
                      <MapPin size={16} className="mr-2 flex-shrink-0 text-primary/70" data-id="element-548" data-mp="scdash85q3s1" />
                      <span className="line-clamp-1" data-id="element-549" data-mp="ee6kbm5jf62e">{apt.location}</span>
                    </div>}
                </div>
              </div>;
      })}
        </div>}

      {/* Create/Edit Bottom Sheet */}
      <AnimatePresence data-id="element-550" data-mp="341xjsojbez8o">
        {showSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => !isSaving && !isDeleting && setShowSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-551" data-mp="33z1l2xzn86j6" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] z-[101] flex flex-col max-h-[90vh] md:max-w-[500px] md:mx-auto md:rounded-[24px] md:bottom-6" data-id="element-552" data-mp="35hjn5y52op28">
              <div className="p-6 pb-4 border-b border-border flex-shrink-0" data-id="element-553" data-mp="2cupgq553bq7r">
                <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-6 md:hidden" data-id="element-554" data-mp="sd6imtfkgfi3" />
                <h2 className="text-[20px] font-semibold text-text-primary" data-id="element-555" data-mp="xbrl6ziajb9p">
                  {editingAppointment ? 'Edit Appointment' : 'New Appointment'}
                </h2>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar flex-1" data-id="element-556" data-mp="1642xlhgkky6o">
                <div className="space-y-5" data-id="element-557" data-mp="166fm6dgu26sp">
                  <div data-id="element-558" data-mp="1b47eh8le3uxf">
                    <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-559" data-mp="1b2wcnebi8x8y">
                      Title <span className="text-danger" data-id="element-560" data-mp="1g0xujvx72qiu">*</span>
                    </label>
                    <InputField id="title" placeholder="e.g. Prenatal checkup" value={formData.title} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} data-id="element-561" data-mp="1l2n5lbkf346x" />
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-562" data-mp="y2674izyvpw3">
                    <div data-id="element-563" data-mp="t20nnq3up9p3">
                      <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-564" data-mp="t2jxejjckrs1">
                        Date <span className="text-danger" data-id="element-565" data-mp="1723212cy9kzl">*</span>
                      </label>
                      <input type="date" value={formData.date} onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} className="w-full h-[52px] rounded-[12px] border border-border bg-white text-text-primary px-4 focus:border-primary focus:outline-none transition-colors" data-id="element-566" data-mp="12xljt7m8nfqx" />
                    </div>
                    <div data-id="element-567" data-mp="1uqukv34ngqzp">
                      <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-568" data-mp="1urnj1fpjm868">
                        Time <span className="text-danger" data-id="element-569" data-mp="2edfmeyo1glj0">*</span>
                      </label>
                      <input type="time" value={formData.time} onChange={e => setFormData({
                    ...formData,
                    time: e.target.value
                  })} className="w-full h-[52px] rounded-[12px] border border-border bg-white text-text-primary px-4 focus:border-primary focus:outline-none transition-colors" data-id="element-570" data-mp="2j9wdo1qjbg0b" />
                    </div>
                  </div>

                  <div data-id="element-571" data-mp="1qnldutar5j4k">
                    <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-572" data-mp="1qn36b0in3ihc">
                      Doctor Name{' '}
                      <span className="text-text-secondary font-normal" data-id="element-573" data-mp="1hlvn7pop2o7u">
                        (optional)
                      </span>
                    </label>
                    <InputField id="doctorName" placeholder="Dr. Smith" icon={<User size={20} data-id="element-575" data-mp="1s2z6ohnv0fq3" />} value={formData.doctorName} onChange={e => setFormData({
                  ...formData,
                  doctorName: e.target.value
                })} data-id="element-574" data-mp="1x3d6jcfb4jd1" />
                  </div>

                  <div data-id="element-576" data-mp="19d9abq4g7lw2">
                    <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-577" data-mp="19czngbep9uul">
                      Location{' '}
                      <span className="text-text-secondary font-normal" data-id="element-578" data-mp="zg4qhwy2s2if">
                        (optional)
                      </span>
                    </label>
                    <InputField id="location" placeholder="Hospital or clinic name" icon={<MapPin size={20} data-id="element-580" data-mp="322s59y5vto4u" />} value={formData.location} onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} data-id="element-579" data-mp="vdq5p7auisoi" />
                  </div>

                  <div data-id="element-581" data-mp="3g21ak2sjuxu1">
                    <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-582" data-mp="3g0go70ge2l79">
                      Notes{' '}
                      <span className="text-text-secondary font-normal" data-id="element-583" data-mp="2ndm6ucevc1y0">
                        (optional)
                      </span>
                    </label>
                    <textarea className="w-full h-[100px] rounded-[12px] border border-border bg-white text-text-primary placeholder:text-text-secondary transition-all duration-300 outline-none focus:border-primary focus:shadow-focus p-4 resize-y" placeholder="Any questions for the doctor..." value={formData.notes} onChange={e => {
                  if (e.target.value.length <= 300) {
                    setFormData({
                      ...formData,
                      notes: e.target.value
                    });
                  }
                }} data-id="element-584" data-mp="2savv3oqiu7k9" />
                    <div className={`text-right text-[12px] mt-1 ${formData.notes.length >= 300 ? 'text-danger' : 'text-text-secondary'}`} data-id="element-585" data-mp="sdyyrpa1wfqw">
                      {formData.notes.length} / 300
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-border bg-white pb-[calc(24px+env(safe-area-inset-bottom))] md:pb-6 md:rounded-b-[24px] flex-shrink-0" data-id="element-586" data-mp="173dbn7cu5fb1">
                {showDeleteConfirm ? <div className="bg-danger/10 rounded-[16px] p-4 animate-fadeIn" data-id="element-587" data-mp="em0v3tz3zc24">
                    <p className="text-[15px] font-medium text-danger text-center mb-4" data-id="element-588" data-mp="731h63uf2cq5">
                      Delete this appointment?
                    </p>
                    <div className="flex gap-3" data-id="element-589" data-mp="24hu20436uw7">
                      <button onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} className="flex-1 h-[44px] bg-white text-text-primary rounded-pill font-medium border border-border" data-id="element-590" data-mp="3t9q7pyazgbcd">
                        Cancel
                      </button>
                      <button onClick={handleDelete} disabled={isDeleting} className="flex-1 h-[44px] bg-danger text-white rounded-pill font-medium flex items-center justify-center" data-id="element-591" data-mp="3fhjscefnm8dz">
                        {isDeleting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-592" data-mp="30nezc1h4x650" /> : 'Yes, Delete'}
                      </button>
                    </div>
                  </div> : <div className="space-y-3" data-id="element-593" data-mp="2cygnr5byurj4">
                    <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.title || !formData.date || !formData.time} data-id="element-594" data-mp="2czrsp8h5e5bt">
                      {editingAppointment ? 'Save Changes' : 'Save Appointment'}
                    </Button>

                    {editingAppointment && <button onClick={() => setShowDeleteConfirm(true)} className="w-full h-[44px] flex items-center justify-center text-danger font-medium text-[15px] hover:bg-danger/5 rounded-pill transition-colors" data-id="element-595" data-mp="2vuymi6p8frov">
                        <Trash2 size={18} className="mr-2" data-id="element-596" data-mp="3tejlhmi7heln" />
                        Delete Appointment
                      </button>}
                  </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <style data-id="element-597" data-mp="gb652wmmitcd">{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #E5E2DD; border-radius: 10px; }
      `}</style>
    </main>;
}